import React, { useState, useEffect } from 'react';
import { PhoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/outline';
import { useCallManagement } from '../contexts/CallManagementContext';

const FloatingCallIcon: React.FC = () => {
  const { liveCall, isMinimized, setShowEndCallModal, setEndCallDurationSnapshot, showEndCallModal, endCallDurationSnapshot } = useCallManagement();
  const [displayDuration, setDisplayDuration] = useState(0);

  const calculateDuration = (): number => {
    if (!liveCall?.actualStartTime) return 0;
    const startTime = new Date(liveCall.actualStartTime);
    const now = new Date();
    return Math.floor((now.getTime() - startTime.getTime()) / 1000);
  };

  // Run timer only when live call is active and End Call modal is NOT open (stops timer at click)
  useEffect(() => {
    if (!liveCall || !isMinimized || showEndCallModal) {
      return;
    }
    if (!liveCall.actualStartTime) return;

    const updateDisplay = () => setDisplayDuration(calculateDuration());
    updateDisplay();
    const interval = setInterval(updateDisplay, 1000);
    return () => clearInterval(interval);
  }, [liveCall, isMinimized, showEndCallModal]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const handleEndCallClick = () => {
    if (!liveCall) return;
    // Capture duration at exact moment of click so saved duration is correct
    const durationAtClick = calculateDuration();
    setEndCallDurationSnapshot(durationAtClick);
    setShowEndCallModal(true);
  };

  if (!liveCall || !isMinimized) {
    return null;
  }

  return (
    <>
      {/* Floating Call Widget - Always visible with timer and end button */}
      <div className="fixed bottom-6 right-6 z-[9999]">
        <div className="bg-white rounded-lg shadow-2xl border-2 border-red-500 p-4 min-w-[280px]">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-gray-900">Live Call</span>
            </div>
          </div>

          {/* Lead Info */}
          <div className="mb-3">
            <div className="text-base font-medium text-gray-900">
              {liveCall.leadFirstName} {liveCall.leadLastName}
            </div>
            <div className="text-xs text-gray-500">{liveCall.phoneNumber}</div>
          </div>

          {/* Timer: frozen when End Call modal is open, otherwise live */}
          <div className="bg-gray-50 rounded-lg p-3 mb-3">
            <div className="text-xs text-gray-500 mb-1">Call Duration</div>
            <div className="text-2xl font-mono font-bold text-blue-600">
              {formatDuration(showEndCallModal && endCallDurationSnapshot !== null ? endCallDurationSnapshot : displayDuration)}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleEndCallClick}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
            >
              <PhoneXMarkIcon className="w-4 h-4 mr-2" />
              End Call
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingCallIcon;
